import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { TemplateState } from '../../../../types/common/types';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import {
  accountsNodeOptions,
  INVALID_ENTITIES_FOR_CUSTOM_FOLDERS,
  PACKAGES_AVAILABLE_TARGETS,
  ServiceTargets,
  SettingsFormData,
  SettingsFormViewMetadata,
  VALID_ENTITIES_FOR_CUSTOM_FOLDERS,
} from '../types/PackageSettingsFormViewData';
import {
  ConsoleRequestSettings,
  ServiceTargetIds,
  TargetsDependencies,
  TargetsViewData,
} from '../../../../targets/type';
import { PackageSettingsEvent } from '../types/PackageSettingsEvent';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { CustomizableValidator } from '../../../../ui/gzForm/validators/customizable.validator';
import { ValidationResult } from '../../../../ui/gzForm/formField/types/form-validator';
import { PackageConstants } from '../../../PackageConstants';
import { MinLengthValidator } from '../../../../ui/gzForm/validators/min-length.validator';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { GzTextWebComponent } from '../../../../ui/gzText';
import TargetsWebComponent from '../../../../targets/component/TargetsWebComponent';
import { Feature } from '@nirvana/shared/constants';
import { PackageType } from '../../../packagesGrid/types';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { translateFn } from '../../../../utils/translator.helper';

export class PackageSettingsFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;

  public dom: {
    $wrapper: HTMLElement;
    $settingsSectionSubtitle: GzTitleWebComponent;
    $settingsTargets: TargetsWebComponent;
    $useCustomPathCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $useCustomFoldersCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $setUninstallPasswordCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $customPathInput: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $settingsPasswordInput: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $settingsConfirmPasswordInput: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $settingsPasswordInfo: GzTextWebComponent;
    $settingsPasswordSection: HTMLElement;
  };
  private companyId: string;
  private settingsFormData: SettingsFormData | null;
  private passwordChanged = true;
  private packageType: PackageType;

  public getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('#package-settings-form-wrapper'),
      $settingsSectionSubtitle: this.shadow.querySelector('#settings-subtitle'),
      $useCustomPathCheckbox: this.shadow.querySelector('#useCustomPathFormField'),
      $useCustomFoldersCheckbox: this.shadow.querySelector('#settingsUseCustomFoldersFormField'),
      $setUninstallPasswordCheckbox: this.shadow.querySelector('#settingsSetUninstallPasswordFormField'),
      $customPathInput: this.shadow.querySelector('#customPathInputFormField'),
      $settingsPasswordInput: this.shadow.querySelector('#settingsPasswordFormField'),
      $settingsConfirmPasswordInput: this.shadow.querySelector('#settingsConfirmPasswordFormField'),
      $settingsPasswordSection: this.shadow.querySelector('#settingsPasswordSection'),
      $settingsPasswordInfo: this.shadow.querySelector('#password-info'),
      $settingsTargets: this.shadow.querySelector('#settingsTargets'),
    };
  }

  public buildSuccessView(viewData: SettingsFormViewMetadata): void {
    this.companyId = viewData.companyId;
    this.packageType = viewData.packageType;
    this.createFormGroup();
    this.setTranslatedStrings();
    this.prepareTargetsComponent();
    this.settingsFormData = viewData.settingsFormData;

    if (viewData.settingsFormData === null) {
      this.initAddForm();
    } else {
      this.initEditForm();
    }
  }

  private initAddForm(): void {
    this.setDefaultState();
    this.enableDisableSettingsSection(true);
  }

  private initEditForm(): void {
    this.enableDisableSettingsSection(false);

    if (this.settingsFormData?.setUninstallPassword) {
      this.dom.$settingsPasswordInput.value = this.settingsFormData.uninstallPassword as string;
    }

    this.passwordChanged = this.settingsFormData?.uninstallPassword === '';
    this.formGroup.value = {
      setUninstallPassword: this.settingsFormData?.setUninstallPassword ?? false,
      settings: {
        useCustomInstallationPath: this.settingsFormData?.useCustomInstallationPath ?? false,
        customInstallationPath: this.settingsFormData?.customInstallationPath ?? '',
        useCustomDeployFolder: this.settingsFormData?.useCustomDeployFolder ?? false,
        uninstallPassword: this.settingsFormData?.uninstallPassword ?? '',
      },
    };
  }

  private createFormGroup(): void {
    this.formGroup = FormGroup.build({
      setUninstallPassword: this.dom.$setUninstallPasswordCheckbox,
      settings: {
        useCustomInstallationPath: this.dom.$useCustomPathCheckbox,
        customInstallationPath: this.dom.$customPathInput,
        useCustomDeployFolder: this.dom.$useCustomFoldersCheckbox,
        uninstallPassword: this.dom.$settingsConfirmPasswordInput,
      },
    });
  }

  setTranslatedStrings(): void {
    this.dom.$settingsSectionSubtitle.innerText = translateFn('Settings');
    this.dom.$useCustomPathCheckbox.Input.label = translateFn('Use custom installation path');
    this.dom.$setUninstallPasswordCheckbox.Input.label = translateFn('Set uninstall password');
    this.dom.$useCustomFoldersCheckbox.Input.label = translateFn('Use custom folders');

    this.dom.$customPathInput.Input.placeholder = translateFn('Specific path');
    this.dom.$settingsPasswordInput.label = translateFn('Password');
    this.dom.$settingsPasswordInput.Input.placeholder = translateFn('Click here to change your password');
    this.dom.$settingsConfirmPasswordInput.label = translateFn('Confirm password');
    this.dom.$settingsConfirmPasswordInput.Input.placeholder = translateFn('Please re-enter password');
    this.dom.$settingsPasswordInfo.innerText = translateFn(
      'The password must contain at least {minLength} characters and comply with the following complexity: at least one digit, one upper case, one lower case and one special character.',
      {
        minLength: PackageConstants.Password.MinLength,
      }
    );
  }

  /**
   * On change company or add
   * @private
   */
  private setDefaultState(): void {
    toggleVisibility(this.dom.$settingsTargets, false);
    this.formGroup.value = {
      setUninstallPassword: false,
      settings: {
        useCustomInstallationPath: false,
        customInstallationPath: '',
        useCustomDeployFolder: false,
        uninstallPassword: '',
      },
    };

    if (this.settingsFormData?.setUninstallPassword) {
      this.dom.$settingsPasswordInput.Input.value = '';
    }
  }

  private prepareTargetsComponent(): void {
    const selectTargetsDependency = {
      translateFn: translateFn,
      api: {
        type: 'eps',
      },
      isCloud: this.dependencies.isCloud,
      getUser: this.dependencies.getUser,
    } as TargetsDependencies;

    this.dom.$settingsTargets.injectDependencies(selectTargetsDependency);
    this.dom.$settingsTargets.setOptionsForGetChildrenNodes(accountsNodeOptions);
    this.dom.$settingsTargets.setDataProviderConsoleRequestSettings({
      EPS: {
        rootNode: {
          method: 'getRootForPackages',
        },
      },
      SVE: {
        rootNode: {
          method: 'getRootForPackages',
        },
      },
    } as ConsoleRequestSettings);
  }

  private async startTargetsComponent(): Promise<void> {
    const targetsViewData = {
      companyId: this.companyId ? this.companyId : this.dependencies.getCurrentUserCompany().id,
      availableServices: this.dependencies.moduleAvailableServices,
      preSelectedTargets: this.getPreselectedTargets(),
      showExcludedFolder: true,
      invalidTargetTypes: INVALID_ENTITIES_FOR_CUSTOM_FOLDERS,
    } as TargetsViewData;

    try {
      this.dom.$settingsTargets.setViewData(targetsViewData);
      this.dom.$settingsTargets.setIsSingleSelect(true);
      if (!this.dom.$settingsTargets.started) {
        await this.dom.$settingsTargets.start();
      } else {
        await this.dom.$settingsTargets.controller.render();
      }
      this.dom.$settingsTargets.hideSelectTargetsTitle();
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * First filter Services available for packages
   * Filter services targets to return only targets VALID_ENTITIES_FOR_CUSTOM_FOLDERS
   * @private
   */
  private getPreselectedTargets(): ServiceTargets[] {
    const serviceTargets = this.getServiceTargets();
    return serviceTargets
      .filter(target => {
        return PACKAGES_AVAILABLE_TARGETS.indexOf(target.serviceId) !== -1;
      })
      .map(serviceTarget => {
        return {
          serviceId: serviceTarget.serviceId,
          targets: serviceTarget.targets.filter(target => {
            //this flag si enriched from backend on readRecords method when edit
            // on add this flag is not set
            const hasAccessToFolder = target.hasAccessToFolder ?? true;
            if (target.type) {
              return VALID_ENTITIES_FOR_CUSTOM_FOLDERS.indexOf(target.type) !== -1 && hasAccessToFolder;
            } else {
              return;
            }
          }),
        };
      });
  }

  /**
   * Get preselected targets
   * On edit read from packageData, on Create read from userServiceTargets
   * @private
   */
  private getServiceTargets(): Array<ServiceTargets> {
    if (this.settingsFormData?.useCustomDeployFolder && this?.settingsFormData.customDeployFolders) {
      //edit flow
      const serviceTargets: Array<ServiceTargets> = [];
      const epsNotSet = this.settingsFormData?.customDeployFolders?.epsFolder?.notSet === true;
      if (this.settingsFormData?.customDeployFolders?.epsFolder && !epsNotSet) {
        serviceTargets.push({
          serviceId: ServiceTargetIds.EPS,
          targets: [this.settingsFormData.customDeployFolders.epsFolder],
        });
      }

      const sveNotSet = this.settingsFormData?.customDeployFolders?.sveFolder?.notSet === true;
      if (this.settingsFormData?.customDeployFolders?.sveFolder && !sveNotSet) {
        serviceTargets.push({
          serviceId: ServiceTargetIds.SVE,
          targets: [this.settingsFormData.customDeployFolders.sveFolder],
        });
      }
      return serviceTargets;
    }
    //add flow
    return this.dependencies.getUser().serviceTargets;
  }

  buildErrorView(error: Error): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public onCustomPathCheckboxChanged(checked: boolean): void {
    this.CustomPathInputRequiredValidation(checked);
    this.dom.$customPathInput.Input.disabled = !checked;
  }

  public onUninstallPasswordCheckboxChanged(checked: boolean): void {
    this.dom.$settingsPasswordSection.hidden = !checked;
    this.addUninstallPasswordRequiredValidation(checked);
  }

  public onPasswordInputChange(): void {
    if (!this.passwordChanged) {
      this.passwordChanged = true;
      this.addUninstallPasswordRequiredValidation(true);
    }
  }

  public async onUseCustomFoldersChange(checked: boolean): Promise<void> {
    if (this.dom.$settingsTargets.controller.view.formGroup) {
      this.dom.$settingsTargets.controller.view.formGroup.setDisabled(!checked);
    }

    toggleVisibility(this.dom.$settingsTargets, checked);

    if (checked) {
      await this.startTargetsComponent();

      this.dom.$wrapper.dispatchEvent(
        new CustomEvent(PackageSettingsEvent.OnUseCustomFolderChange, {
          detail: {
            checked: checked,
          },
          bubbles: true,
          composed: true,
        })
      );
    }
  }

  private CustomPathInputRequiredValidation(state: boolean): void {
    if (state) {
      this.dom.$customPathInput.Validators = [
        RequiredValidator(this.commonStrings.requiredField),
        CustomizableValidator(this.validatePath.bind(this)),
      ];

      this.dom.$customPathInput.setAttribute('required', 'required');
    } else {
      this.dom.$customPathInput.Validators = [];
      this.dom.$customPathInput.removeAttribute('required');
    }
  }

  private addUninstallPasswordRequiredValidation(state: boolean): void {
    //on init we validate only after the password was changed
    if (!this.passwordChanged) {
      this.dom.$settingsPasswordInput.Validators = [];
      this.dom.$settingsConfirmPasswordInput.Validators = [];
      return;
    }

    const passwordValidators = [
      RequiredValidator(this.commonStrings.requiredField),
      MinLengthValidator(
        PackageConstants.Password.MinLength,
        translateFn('Password must be at least {minLength} characters in length.', {
          minLength: PackageConstants.Password.MinLength,
        })
      ),
      MaxLengthValidator(
        PackageConstants.Password.MaxLength,
        translateFn('Password must not be longer than {maxLength} characters in length', {
          maxLength: PackageConstants.Password.MaxLength,
        })
      ),
      RegexValidator(
        /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[~!@#$%^&*_\-+=`|(){}\[\]:;"'<>,.?\/]).*$/,
        translateFn(
          'The password must contain at least one digit, one upper case, one lower case and one special character.'
        )
      ),
    ];

    const confirmPasswordValidators = [...passwordValidators];
    confirmPasswordValidators.push(
      CustomizableValidator(value => {
        if (value !== this.dom.$settingsPasswordInput.value) {
          return {
            valid: false,
            error: translateFn('Passwords do not match'),
          };
        }
        if (!this.dom.$settingsPasswordInput.valid) {
          this.dom.$settingsPasswordInput.validate();
        }
        return {
          valid: true,
        };
      })
    );

    passwordValidators.push(
      CustomizableValidator(value => {
        if (value !== this.dom.$settingsConfirmPasswordInput.value) {
          return {
            valid: false,
            error: translateFn('Passwords do not match'),
          };
        }

        if (!this.dom.$settingsConfirmPasswordInput.valid) {
          this.dom.$settingsConfirmPasswordInput.validate();
        }
        return {
          valid: true,
        };
      })
    );

    if (state) {
      this.dom.$settingsPasswordInput.Validators = passwordValidators;
      this.dom.$settingsPasswordInput.setAttribute('required', 'required');

      this.dom.$settingsConfirmPasswordInput.Validators = confirmPasswordValidators;
      this.dom.$settingsConfirmPasswordInput.setAttribute('required', 'required');
    } else {
      this.dom.$settingsPasswordInput.Validators = [];
      this.dom.$settingsPasswordInput.removeAttribute('required');
      this.dom.$settingsConfirmPasswordInput.Validators = [];
      this.dom.$settingsConfirmPasswordInput.removeAttribute('required');
    }
  }

  private validatePath(value: string): ValidationResult {
    if (!this.dependencies.validatePath(value)) {
      return {
        valid: false,
        error: translateFn('Invalid path'),
      };
    }

    return {
      valid: true,
    };
  }

  public enableDisableSettingsSection(disableState: boolean): void {
    if (
      this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_FRAT_VISIBLE) ||
      this.packageType === PackageType.Frat ||
      this.packageType === PackageType.RelayForFrat
    ) {
      this.formGroup.setDisabled(true);
      return;
    }

    this.formGroup.setDisabled(disableState);
    //if the section is already disabled we don't want to override
    if (!disableState) {
      this.dom.$useCustomFoldersCheckbox.disabled = !this.dependencies.getUser().hasCGTargets;
    }
    this.dom.$customPathInput.disabled = !this.dom.$useCustomPathCheckbox.value;
  }

  public settingsTargetsStarted() {
    return this.dom.$useCustomFoldersCheckbox.Input.checked && this.dom.$settingsTargets.controller.view.formGroup;
  }
}
